// @herefor/sharing — privacy-safe navigation shared into family conversations.
//
// A WhatsApp snippet carries an ORDINARY authenticated app route. The route is not
// a credential and grants no data: the Convex boundary still resolves identity,
// active Membership and `plan.view` after it opens (AUT-001/005, SHA-009).
//
// This package owns the route construction so private words can never quietly move
// into a query string in one UI surface. It deliberately knows nothing about React,
// Convex, people, events or need labels.

const PUBLIC_PLAN_REF_BYTES = 16;
const PUBLIC_PLAN_REF_PATTERN = /^[A-Za-z0-9_-]{22}$/u;
const BASE64URL_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
const CONVEX_ITEM_REF_PATTERN = /^[a-z0-9]{31,32}$/u;

export type SecureRandomBytes = (byteLength: number) => Uint8Array;

/**
 * Create the random, stable route identity required by CIR-018.
 *
 * The caller supplies its platform's cryptographically secure randomness seam;
 * this package owns the canonical 128-bit, unpadded base64url representation. A
 * generated ref is always exactly the shape accepted by the share-link boundary.
 */
export function generatePublicPlanRef(secureRandomBytes: SecureRandomBytes): string {
  const bytes = secureRandomBytes(PUBLIC_PLAN_REF_BYTES);
  if (bytes.byteLength !== PUBLIC_PLAN_REF_BYTES) {
    throw new RangeError("publicPlanRef generation requires 16 random bytes");
  }

  let encoded = "";
  for (let index = 0; index < bytes.length; index += 3) {
    const first = bytes[index];
    if (first === undefined) break;
    const second = bytes[index + 1];
    const third = bytes[index + 2];
    const chunk = (first << 16) | ((second ?? 0) << 8) | (third ?? 0);

    encoded += BASE64URL_ALPHABET.charAt((chunk >>> 18) & 63);
    encoded += BASE64URL_ALPHABET.charAt((chunk >>> 12) & 63);
    if (second !== undefined) encoded += BASE64URL_ALPHABET.charAt((chunk >>> 6) & 63);
    if (third !== undefined) encoded += BASE64URL_ALPHABET.charAt(chunk & 63);
  }

  return encoded;
}

export type ShareLinkRejection =
  "invalid_origin" | "invalid_public_plan_ref" | "invalid_support_need_ref";

export type ShareLinkResult =
  | { readonly ok: true; readonly value: string }
  | { readonly ok: false; readonly reason: ShareLinkRejection };

export interface AuthenticatedNeedShareLinkInput {
  /** `window.location.origin` in the browser; injected so this function stays pure. */
  readonly origin: string;
  /** Stable, random circle route reference. It is opaque navigation, not a secret. */
  readonly publicPlanRef: string;
  /** Opaque item id already used by the authenticated need-detail route. */
  readonly supportNeedId: string;
}

/**
 * Build the one link a need snippet may contain.
 *
 * No query string, fragment or caller-supplied path is accepted. Production must be
 * HTTPS; loopback HTTP remains available for the real local Playwright deployment.
 */
export function authenticatedNeedShareLink(
  input: AuthenticatedNeedShareLinkInput,
): ShareLinkResult {
  const origin = safeOrigin(input.origin);
  if (origin === null) return { ok: false, reason: "invalid_origin" };
  if (!PUBLIC_PLAN_REF_PATTERN.test(input.publicPlanRef)) {
    return { ok: false, reason: "invalid_public_plan_ref" };
  }
  if (!CONVEX_ITEM_REF_PATTERN.test(input.supportNeedId)) {
    return { ok: false, reason: "invalid_support_need_ref" };
  }

  return {
    ok: true,
    value: `${origin}/c/${input.publicPlanRef}/needs/${input.supportNeedId}`,
  };
}

/** First chosen-name word for the discreet outbound template. */
export function discreetFirstName(chosenName: string): string | null {
  const first = chosenName.trim().split(/\s+/u)[0];
  return first === undefined || first === "" ? null : first;
}

function safeOrigin(raw: string): string | null {
  // An origin supplied by `window.location.origin` has no path, credentials,
  // query or fragment. Match that exact grammar instead of normalising a wider URL.
  if (/^https:\/\/[A-Za-z0-9.-]+(?::\d{1,5})?$/u.test(raw)) return raw;
  if (/^http:\/\/(?:localhost|127\.0\.0\.1|\[::1\])(?::\d{1,5})?$/u.test(raw)) return raw;
  return null;
}
